import baseApi from 'shared/api/baseApi';

import type {
  CreateCredentialBody,
  CreateIntegrationBody,
  Integration,
  IntegrationCredential,
  IntegrationConfig,
  IntegrationDetail,
  IntegrationMapping,
  IntegrationProcess,
  MappingBody,
  UpdateConfigBody,
  UpdateCredentialBody,
  UpdateIntegrationBody,
  WebhookSlugResponse,
  WebhookTokenResponse,
} from '../model/integration';

const BASE_URL = '/admin/integrations';

const integrationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getIntegrations: build.query<Integration[], number>({
      query: (projectId) => ({ url: BASE_URL, method: 'GET', params: { projectId } }),
      providesTags: [{ type: 'Integrations', id: 'LIST' }],
    }),
    getIntegration: build.query<IntegrationDetail, number>({
      query: (id) => ({ url: `${BASE_URL}/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Integrations', id }],
    }),
    createIntegration: build.mutation<Integration, CreateIntegrationBody>({
      query: (body) => ({ url: BASE_URL, method: 'POST', body }),
      invalidatesTags: [{ type: 'Integrations', id: 'LIST' }],
    }),
    updateIntegration: build.mutation<Integration, { id: number; body: UpdateIntegrationBody }>({
      query: ({ id, body }) => ({ url: `${BASE_URL}/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Integrations', id },
        { type: 'Integrations', id: 'LIST' },
      ],
    }),

    getCredentials: build.query<IntegrationCredential[], string>({
      query: (provider) => ({ url: `${BASE_URL}/credentials`, method: 'GET', params: { provider } }),
      providesTags: (_result, _error, provider) => [{ type: 'IntegrationCredentials', id: provider }],
    }),
    createCredential: build.mutation<IntegrationCredential, CreateCredentialBody>({
      query: (body) => ({ url: `${BASE_URL}/credentials`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { provider }) => [{ type: 'IntegrationCredentials', id: provider }],
    }),
    // The integration detail embeds its credential's settings, so every open detail is refreshed too
    updateCredential: build.mutation<
      IntegrationCredential,
      { id: number; provider: string; body: UpdateCredentialBody }
    >({
      query: ({ id, body }) => ({ url: `${BASE_URL}/credentials/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { provider }) => [
        { type: 'IntegrationCredentials', id: provider },
        'Integrations',
      ],
    }),

    createMappings: build.mutation<IntegrationMapping[], { integrationId: number; mappings: MappingBody[] }>({
      query: ({ integrationId, mappings }) => ({
        url: `${BASE_URL}/${integrationId}/mappings`,
        method: 'POST',
        body: mappings,
      }),
      invalidatesTags: (_result, _error, { integrationId }) => [{ type: 'Integrations', id: integrationId }],
    }),
    updateMapping: build.mutation<
      IntegrationMapping,
      { id: number; integrationId: number; body: Partial<MappingBody> }
    >({
      query: ({ id, body }) => ({ url: `${BASE_URL}/mappings/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { integrationId }) => [{ type: 'Integrations', id: integrationId }],
    }),
    deleteMapping: build.mutation<void, { id: number; integrationId: number }>({
      query: ({ id }) => ({ url: `${BASE_URL}/mappings/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { integrationId }) => [{ type: 'Integrations', id: integrationId }],
    }),

    createProcess: build.mutation<IntegrationProcess, { integrationId: number; type: string; cron: string | null }>({
      query: ({ integrationId, ...body }) => ({ url: `${BASE_URL}/${integrationId}/processes`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { integrationId }) => [{ type: 'Integrations', id: integrationId }],
    }),
    updateProcess: build.mutation<
      IntegrationProcess,
      { id: number; integrationId: number; body: { enabled?: boolean; cron?: string | null } }
    >({
      query: ({ id, body }) => ({ url: `${BASE_URL}/processes/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { integrationId }) => [{ type: 'Integrations', id: integrationId }],
    }),
    // Only enqueues the run: the page polls the detail afterwards to see runningSince and lastResult move
    runProcess: build.mutation<{ processId: number }, { id: number; integrationId: number }>({
      query: ({ id }) => ({ url: `${BASE_URL}/processes/${id}/run`, method: 'POST', body: { dryRun: false } }),
      invalidatesTags: (_result, _error, { integrationId }) => [{ type: 'Integrations', id: integrationId }],
    }),
    // Runs in the request, skipping the queue, and answers the process with the dry run's lastResult
    dryRunProcess: build.mutation<IntegrationProcess, { id: number; integrationId: number }>({
      query: ({ id }) => ({ url: `${BASE_URL}/processes/${id}/run`, method: 'POST', body: { dryRun: true } }),
      invalidatesTags: (_result, _error, { integrationId }) => [{ type: 'Integrations', id: integrationId }],
    }),

    getIntegrationConfig: build.query<IntegrationConfig, number>({
      query: (id) => ({ url: `${BASE_URL}/${id}/config`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'IntegrationConfig', id }],
    }),
    // Replaces one layer of the provider's config rule; the API rejects it if any layer would stop resolving
    updateIntegrationConfig: build.mutation<IntegrationConfig, { id: number; body: UpdateConfigBody }>({
      query: ({ id, body }) => ({ url: `${BASE_URL}/${id}/config`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'IntegrationConfig', id }],
    }),

    // Creates the slug of the integration's webhook URLs, or replaces it: the old URLs stop answering
    createWebhookSlug: build.mutation<WebhookSlugResponse, number>({
      query: (integrationId) => ({ url: `${BASE_URL}/${integrationId}/webhook-slug`, method: 'POST' }),
      invalidatesTags: (_result, _error, integrationId) => [
        { type: 'Integrations', id: integrationId },
        { type: 'Integrations', id: 'LIST' },
      ],
    }),
    // A new Bearer token for every webhook of the integrations using this credential, replacing the one it had
    createWebhookToken: build.mutation<WebhookTokenResponse, { credentialId: number; provider: string }>({
      query: ({ credentialId }) => ({ url: `${BASE_URL}/credentials/${credentialId}/webhook-token`, method: 'POST' }),
      invalidatesTags: (_result, _error, { provider }) => [{ type: 'IntegrationCredentials', id: provider }],
    }),
    // Makes those webhooks public again: only the slug protects them
    deleteWebhookToken: build.mutation<void, { credentialId: number; provider: string }>({
      query: ({ credentialId }) => ({ url: `${BASE_URL}/credentials/${credentialId}/webhook-token`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { provider }) => [{ type: 'IntegrationCredentials', id: provider }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetIntegrationsQuery,
  useGetIntegrationQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useGetCredentialsQuery,
  useCreateCredentialMutation,
  useUpdateCredentialMutation,
  useCreateMappingsMutation,
  useUpdateMappingMutation,
  useDeleteMappingMutation,
  useCreateProcessMutation,
  useUpdateProcessMutation,
  useRunProcessMutation,
  useDryRunProcessMutation,
  useGetIntegrationConfigQuery,
  useUpdateIntegrationConfigMutation,
  useCreateWebhookSlugMutation,
  useCreateWebhookTokenMutation,
  useDeleteWebhookTokenMutation,
} = integrationsApi;
